import React, { Fragment, useMemo } from 'react';
import { observer } from 'mobx-react';
import ThemedSpinner from '../themed/ThemedSpinner';
import SimpleTable from './SimpleTable';
import { IconButton, CircularProgress } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '@material-ui/core/Button';
import EditIcon from '@material-ui/icons/Edit';
import { formatDate } from '../../utils/helpers';
import { useStores } from '../../hooks/use-stores';
import { Box } from '@mui/material';

function Packages({
    company,
    removingPackages,
    packages,
    removePackage,
    onEditHere,
}) {
    const { commonStore } = useStores();
    
    const handleDeletePackage = (packageData) => {
        const isLinkedToChargebee = packageData.external_id &&
            !packageData.external_id.startsWith('partner') &&
            !packageData.external_id.startsWith('company');

        const description = isLinkedToChargebee ? (
            <>
                Package will be completely removed from our database.<br/><br/>Subscription will be <strong>completely deleted in Chargebee</strong> without issuing any new invoices, dues, credit notes, etc.<br/><br/>If you want to <strong>cancel subscription</strong>, do it in Chargebee manually.
            </>
        ) : (
            'Package will be completely removed from our database.'
        );

        commonStore.showConfirm(
            description,
            'Delete',
            'Are you sure you want to delete this package?',
            () => removePackage(packageData.package_id),
            true,
            true
        );
    };

    const isCustomBilling = (company?.settings || []).some(
        (s) => s.name === 'billing.custom' && s.value === '1'
    );
    
    const columns = useMemo(() => [
        {
            Header: 'Name',
            accessor: (x) =>
                (x.external_id && x.external_id.startsWith('partner')) ||
                (x.external_id && x.external_id.startsWith('company'))
                    ? x.external_id
                    : x.package_name,
        },
        {
            Header: 'Chargebee ID',
            accessor: (x) =>
                (x.external_id && x.external_id.startsWith('partner')) ||
                (x.external_id && x.external_id.startsWith('company'))
                    ? 'Not linked'
                    : x.external_id,
        },
        {
            Header: 'Created',
            accessor: 'created',
            Cell: (x) => formatDate(x.value),
        },
        {
            Header: 'Valid from',
            accessor: 'valid_from',
            Cell: (x) => formatDate(x.value),
        },
        {
            Header: 'Valid to',
            accessor: 'valid_to',
            Cell: (x) => formatDate(x.value),
        },
        {
            Header: 'User count',
            accessor: 'users',
        },
        /*{
            Header: 'Package Type',
            accessor: 'full_package',
            Cell: (x) => (x.value === 1 ? 'Full' : 'Partial'),
        },
        {
            Header: 'Videos',
            accessor: (x) =>
                x.full_package ? (
                    'All'
                ) : (
                    <Fragment>{(x.videos || []).length} videos</Fragment>
                ),
        },*/
        {
            Header: 'In Trial',
            accessor: (x) => {
                if (x.trial) {
                    return <strong>TRIAL</strong>;
                }
                if (!x.trial) {
                    return <strong>NO</strong>;
                }
            },
        },
        {
            Header: 'Status',
            accessor: (x) => {
                if (x.valid) {
                    return <strong>VALID</strong>;
                }
                if (!x.valid) {
                    // Check if valid_to is in the past (expired) or valid_from is in the future
                    // Compare dates only, not time
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    const validToDate = x.valid_to ? new Date(x.valid_to) : null;
                    if (validToDate) {
                        validToDate.setHours(0, 0, 0, 0);
                        const isPast = validToDate < today;
                        
                        if (isPast) {
                            return <strong>EXPIRED</strong>;
                        }
                    }
                    return <strong style={{ color: '#ff9800' }}>FUTURE</strong>;
                }
            },
        },
        {
            id: 'editPackage',
            accessor: (x) => {
                // Check if package is valid or future (not expired)
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const validToDate = x.valid_to ? new Date(x.valid_to) : null;
                const isExpired = validToDate && validToDate < today;

                const isDemoPackage =
                    x.package_name &&
                    (x.package_name.toLowerCase().startsWith('demo-plan') ||
                        x.package_name.toLowerCase().includes('demo'));
                const canEdit =
                    company?.partnerId === 1 &&
                    (isCustomBilling || isDemoPackage) &&
                    (x.valid && !isExpired);


                // Allow edit for valid packages or future packages (not expired)
                if (x.valid || !isExpired) {
                    if (
                        x.external_id &&
                        x.external_id.startsWith('partner')
                    ) {
                        return (
                            <Button
                                variant="contained"
                                color="primary"
                                href={`${import.meta.env.REACT_APP_LMS_URL}/partner/${company.partnerId || company.accountId}/companies/${company.company_id}`}
                                startIcon={<EditIcon />}
                            >
                                Partner portal
                            </Button>
                        );
                    }
                }
                if (
                    x.external_id &&
                    !x.external_id.startsWith('company') &&
                    !x.external_id.startsWith('partner')
                ) {
                    return (
                        <Box flexDirection="row" display="flex">
                            {canEdit ? (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() =>
                                        onEditHere && onEditHere(x.package_id)
                                    }
                                    startIcon={<EditIcon />}
                                    style={{ marginRight: '8px' }}
                                >
                                    Edit
                                </Button>
                            ) : null}
                            <Button
                                variant="contained"
                                color="primary"
                                href={`https://awarego.chargebee.com/d/subscriptions/${x.external_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<EditIcon />}
                            >
                                Chargebee
                            </Button>
                        </Box>
                    );
                }
            },
        },
        // Only show delete button for companies with partnerId === 1
        ...(company.partnerId === 1 ? [{
            id: 'removePackage',
            accessor: 'package_id',
            Cell: ({ row }) => {
                const packageId = row.original.package_id;
                const isRemoving = removingPackages.includes(packageId);
                return isRemoving ? (
                    <CircularProgress size={24} />
                ) : (
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDeletePackage(row.original)}
                        color="secondary"
                    >
                        <DeleteIcon />
                    </IconButton>
                );
            },
        }] : []),
    ], [removingPackages, company, handleDeletePackage]);

    return !packages ? (
        <ThemedSpinner />
    ) : (
        <Fragment>
            {packages.length > 0 && (
                <SimpleTable 
                    key={`packages-table-${removingPackages.join('-')}`}
                    columns={columns} 
                    data={packages} 
                />
            )}
        </Fragment>
    );
}

export default observer(Packages);
